import React, { useState, useEffect, useId } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Checkbox } from 'primereact/checkbox';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/_new/Button';
import { moveToDown, moveToTop } from 'utils/array';
import { InputText } from '../../_new/InputText';

type SingleChoiceEditWrapperProps = {
	initialItems: string[];
	initialLabel: string;
	initialText: string;
	required: boolean;
	hiddenFromUsers: boolean;
	onSave: (items: string[], label: string, text: string, required: boolean, hiddenFromUsers: boolean) => void;
	onCancel: () => void;
};

interface EditFormObject {
	option: string;
	questionLabel: string;
	questionText: string;
	isRequired: boolean;
	hiddenFromUsers: boolean;
}

export const SingleChoiceEditWrapper: React.FC<SingleChoiceEditWrapperProps> = ({
	initialItems,
	initialLabel,
	initialText,
	required,
	hiddenFromUsers,
	onSave,
	onCancel,
}) => {
	const { t } = useTranslation();
	const [items, setItems] = useState<string[]>(initialItems);
	const [editing, setEditing] = useState<number | null>(null);
	const requiredCheckboxId = useId();
	const hiddenFromUsersCheckboxId = useId();
	const { control, handleSubmit, reset, setValue } = useForm<EditFormObject>({
		defaultValues: {
			questionLabel: initialLabel,
			questionText: initialText,
			isRequired: required,
			hiddenFromUsers,
		},
	});

	useEffect(() => {
		setValue('questionLabel', initialLabel);
		setValue('isRequired', required);
		setValue('hiddenFromUsers', hiddenFromUsers);
	}, [initialLabel, required, hiddenFromUsers, setValue]);

	const handleAddOption = (e: React.MouseEvent) => {
		e.preventDefault();
		setItems((prevItems) => [...prevItems, '']);
		setEditing(items.length);
		setValue('option', '');
	};

	const handleEditOption = (index: number) => {
		const option = items[index];
		setEditing(index);
		setValue('option', option);
	};

	const handleDeleteOption = (index: number) => {
		setItems((prevItems) => prevItems.filter((_, i) => i !== index));
		setEditing(null);
	};

	const handleFormSubmit = (data: EditFormObject) => {
		setItems((prevItems) => {
			const updatedItems = [...prevItems];
			if (editing !== null) {
				updatedItems[editing] = data.option.trim();
			}
			return updatedItems;
		});
		setEditing(null);
		setValue('option', '');
	};

	const handleSave = (data: EditFormObject) => {
		onSave(items, data.questionLabel, data.questionText, data.isRequired, data.hiddenFromUsers);
	};

	const handleOptionMoveToTop = (index: number) => {
		setItems((prev) => moveToTop(prev, index));
	};

	const handleOptionMoveToDown = (index: number) => {
		setItems((prev) => moveToDown(prev, index));
	};

	return (
		<div>
			<div className="field w-full mt-2">
				<Controller
					name="questionText"
					control={control}
					rules={{ required: t('programPanel.editQuestion.questionTextIsRequired') }}
					render={({ field, fieldState }) => (
						<>
							<label className="ml-2 field-label" htmlFor="text">
								{t('programPanel.editQuestion.questionContent')}
							</label>
							<InputText
								{...field}
								placeholder={t('programPanel.editQuestion.enterQuestionTextPlaceholder')}
							/>
							{fieldState.error && <small className="p-error">{fieldState.error.message}</small>}
						</>
					)}
				/>
			</div>
			<div className="field w-full mt-2">
				<Controller
					name="questionLabel"
					control={control}
					render={({ field, fieldState }) => (
						<>
							<label className="ml-2 field-label" htmlFor="questionLabel">
								{t('programPanel.editQuestion.fieldPlaceholder')}
							</label>
							<InputText
								{...field}
								placeholder={t('programPanel.editQuestion.enterQuestionLabelPlaceholder')}
							/>
							{fieldState.error && <small className="p-error">{fieldState.error.message}</small>}
						</>
					)}
				/>
			</div>
			<div className="mt-3 ml-2">
				<Controller
					name="isRequired"
					control={control}
					render={({ field }) => (
						<Checkbox
							id={requiredCheckboxId}
							{...field}
							inputId="required-checkbox"
							checked={field.value}
							onChange={(e) => setValue('isRequired', e.checked)}
						/>
					)}
				/>
				<label htmlFor={requiredCheckboxId} className="ml-2">
					{t('programPanel.editQuestion.isRequired')}
				</label>
			</div>
			<div className="mt-3 ml-2">
				<Controller
					name="hiddenFromUsers"
					control={control}
					render={({ field }) => (
						<Checkbox
							id={hiddenFromUsersCheckboxId}
							{...field}
							inputId="hiddenFromUsers-checkbox"
							checked={field.value}
							onChange={(e) => setValue('hiddenFromUsers', e.checked)}
						/>
					)}
				/>
				<label className="ml-2 field-label" htmlFor={hiddenFromUsersCheckboxId}>
					{t('programPanel.editQuestion.hiddenFromUsers')}
				</label>
			</div>

			<div className="flex flex-column gap-2 align-items-start my-4">
				<ul className="list-none flex flex-column gap-1">
					{items.map((item, index) => {
						const isFirst = index === 0;
						const isLast = index === items.length - 1;
						return (
							// eslint-disable-next-line react/no-array-index-key
							<li key={`${initialLabel}-${item}-${index}`}>
								{editing === index ? (
									<div className="flex align-items-center gap-2">
										<Controller
											name="option"
											control={control}
											rules={{ required: t('programPanel.editQuestion.optionIsRequired') }}
											render={({ field, fieldState }) => (
												<>
													<InputText
														{...field}
														placeholder={t(
															'programPanel.editQuestion.enterOptionPlaceholder'
														)}
													/>
													{fieldState.error && (
														<small className="p-error">{fieldState.error.message}</small>
													)}
													<Button
														label={t('actions.add')}
														icon="check"
														iconOnly
														variant="primary"
														variantSize="sm"
														onClick={handleSubmit(handleFormSubmit)}
														disabled={!field.value.trim()}
													/>
													<Button
														label={t('actions.cancel')}
														icon="times"
														iconOnly
														variant="danger-text"
														variantSize="sm"
														onClick={() => handleDeleteOption(index)}
													/>
												</>
											)}
										/>
									</div>
								) : (
									<div className="inline-flex align-items-center gap-2 ml-4">
										<span className="flex justify-center mr-2">{item}</span>
										<Button
											label={t('actions.edit')}
											icon="pencil"
											iconOnly
											onClick={() => handleEditOption(index)}
											variant="primary-text"
											variantSize="sm"
										/>
										<Button
											label={t('actions.delete')}
											icon="trash"
											iconOnly
											iconClassName="text-red-500"
											onClick={() => handleDeleteOption(index)}
											variant="danger-text"
											variantSize="sm"
										/>
										<Button
											label={t('actions.moveToUp')}
											icon="arrow-up"
											iconOnly
											variant="grey-text"
											variantSize="sm"
											onClick={() => handleOptionMoveToTop(index)}
											disabled={isFirst}
										/>
										<Button
											label={t('actions.moveToDown')}
											icon="arrow-down"
											iconOnly
											variant="grey-text"
											variantSize="sm"
											onClick={() => handleOptionMoveToDown(index)}
											disabled={isLast}
										/>
									</div>
								)}
							</li>
						);
					})}
				</ul>

				<Button
					label={t('programPanel.editQuestion.addAnswer')}
					icon="plus"
					variant="primary-text"
					variantSize="sm"
					className="ml-2"
					onClick={handleAddOption}
					disabled={editing !== null}
				/>
			</div>

			<div className="flex flex-row gap-2">
				<Button
					label={t('actions.save')}
					variant="primary"
					variantSize="sm"
					onClick={handleSubmit(handleSave)}
				/>
				<Button label={t('actions.cancel')} variant="primary-text" variantSize="sm" onClick={onCancel} />
			</div>
		</div>
	);
};
